export type IssueFixType =
  | "schema"
  | "faq"
  | "meta"
  | "title"
  | "h1"
  | "content"
  | "technical"
  | "entity"
  | "competitor"
  // Gap 2 — card-specific fix types
  | "canonical"
  | "redirect"
  | "robots"
  | "llms"
  | "sitemap"
  | "alt"
  | "disavow"
  | "local"
  | "review"
  | "csv"
  | "brief";

export const RUBY_ISSUE_FIXED_EVENT = "ruby-issue-fixed";

export function dispatchIssueFixed(detail: { issueId: string; domain: string }) {
  window.dispatchEvent(new CustomEvent(RUBY_ISSUE_FIXED_EVENT, { detail }));
}

const FIX_LABEL: Record<IssueFixType, string> = {
  schema: "schema markup",
  faq: "FAQ schema",
  meta: "meta description",
  title: "title tag",
  h1: "H1 heading",
  content: "content update",
  technical: "technical fix",
  entity: "entity / Wikidata link",
  competitor: "competitor gap fix",
  canonical: "canonical tag",
  redirect: "redirect rule",
  robots: "robots.txt file",
  llms: "llms.txt file",
  sitemap: "XML sitemap",
  alt: "image alt text",
  disavow: "disavow file",
  local: "local listing / GBP fix",
  review: "review schema",
  csv: "recommendations CSV",
  brief: "content brief",
};

type CmsSteps = { plugin?: string[]; manual?: string[]; platform?: string[] };

const CMS_STEPS: Record<string, Partial<Record<IssueFixType, CmsSteps>>> = {
  WordPress: {
    schema: {
      plugin: [
        'Install the "Insert Headers and Footers" plugin',
        "Go to Settings → Insert Headers and Footers",
        "Paste the schema JSON-LD in the Header section",
        "Click Save",
      ],
      manual: [
        "Go to Appearance → Theme File Editor",
        "Open header.php (or your child theme header)",
        "Paste the schema code before </head>",
        "Click Update File",
      ],
    },
    faq: {
      plugin: [
        'Install "Schema Pro" or "Rank Math" (FAQ schema support)',
        "Edit the target page in WordPress",
        "Add your FAQ block or schema fields and paste the generated FAQ JSON-LD",
        "Update the page",
      ],
      manual: [
        "Edit the page in the block editor",
        "Switch to Custom HTML block or use theme header injection",
        "Paste the FAQ schema before </head> or in page custom fields",
        "Publish the page",
      ],
    },
    meta: {
      plugin: [
        'Install "Yoast SEO" or "Rank Math"',
        "Edit the page in WordPress",
        "Scroll to the SEO meta box → Meta Description",
        "Paste or adapt the generated description and Update",
      ],
      manual: [
        "Edit the page template or use functions.php filter for wp_head",
        "Output <meta name=\"description\" content=\"...\" /> in the page head",
        "Save and clear cache if you use a caching plugin",
      ],
    },
    title: {
      plugin: [
        'Install "Yoast SEO" or "Rank Math"',
        "Edit the page → SEO title field",
        "Paste the recommended title (keep under ~60 characters)",
        "Update the page",
      ],
      manual: [
        "Edit the page or theme template responsible for <title>",
        "Replace the title tag content with the generated text",
        "Save changes",
      ],
    },
    h1: {
      platform: [
        "Edit the page in the block editor",
        "Set the main Heading block to H1 (only one H1 per page)",
        "Use the generated heading text as the H1 content",
        "Update the page",
      ],
    },
    content: {
      platform: [
        "Edit the target page or post",
        "Add or expand content in the main body (not header/footer)",
        "Use the generated outline or copy as a starting point",
        "Update and request re-indexing in Search Console if needed",
      ],
    },
    technical: {
      plugin: [
        'Use "Insert Headers and Footers" for head-level snippets',
        "Or use a technical SEO plugin (Rank Math, Yoast) for redirects/canonicals",
        "Apply the generated fix in the appropriate plugin section",
        "Save and test the live URL",
      ],
      manual: [
        "Identify whether the fix belongs in .htaccess, functions.php, or theme templates",
        "Apply the generated code or configuration",
        "Clear caches and verify on the live page",
      ],
    },
    entity: {
      platform: [
        "Add Organization schema with sameAs links (see generated JSON-LD)",
        "Update your About page with consistent legal name, address, and social profiles",
        "Create or claim your Wikidata item and link it via sameAs in schema",
        "Re-run Rich Results Test after publishing",
      ],
    },
    competitor: {
      platform: [
        "Review the competitor gap recommendation in your action plan",
        "Create or optimize the suggested page/section in WordPress",
        "Add internal links from related high-traffic pages",
        "Monitor rankings after publish",
      ],
    },
  },
  Shopify: {
    schema: {
      platform: [
        "Online Store → Themes → Edit code",
        "Open theme.liquid",
        "Paste schema JSON-LD before </head>",
        "Save",
      ],
    },
    faq: {
      platform: [
        "Edit the product or page in Shopify admin",
        "Use a FAQ app or custom Liquid section for FAQ schema",
        "Paste the generated FAQ JSON-LD in the page metafield or theme snippet",
        "Save",
      ],
    },
    meta: {
      platform: [
        "Products/Pages → edit item → Search engine listing",
        "Update Meta description with the generated text",
        "Save",
      ],
    },
    title: {
      platform: [
        "Edit product or page → Search engine listing",
        "Update Page title with the generated title",
        "Save",
      ],
    },
    h1: {
      platform: [
        "Edit the page template or page content",
        "Ensure a single H1 uses the recommended heading text",
        "Save",
      ],
    },
    content: {
      platform: [
        "Edit the page or collection description",
        "Add the recommended content blocks",
        "Save",
      ],
    },
    technical: {
      platform: [
        "Themes → Edit code for head snippets, or use Shopify redirects for URL fixes",
        "Apply the technical change in theme.liquid or Navigation → URL redirects",
        "Save and verify live URL",
      ],
    },
    entity: {
      platform: [
        "Add Organization JSON-LD via theme.liquid or a schema app",
        "Align store name and social links with Wikidata sameAs URLs",
        "Save and test with Google Rich Results",
      ],
    },
    competitor: {
      platform: [
        "Create a new page or collection targeting the gap keyword",
        "Add content from the action plan recommendation",
        "Link from homepage or top collections",
      ],
    },
  },
  Wix: {
    schema: {
      platform: [
        "Settings → Custom Code",
        "Add code → Place in Head",
        "Paste schema JSON-LD",
        "Apply",
      ],
    },
    faq: {
      platform: [
        "Use Wix FAQ element or Velo custom code",
        "Add FAQ schema in Head injection via Settings → Custom Code",
        "Publish site",
      ],
    },
    meta: {
      platform: [
        "Pages → SEO basics for the page",
        "Edit Meta description",
        "Publish",
      ],
    },
    title: {
      platform: [
        "Pages → SEO basics",
        "Edit Page title / SEO title",
        "Publish",
      ],
    },
    h1: {
      platform: [
        "Edit the page",
        "Set the main title element as the single H1 with recommended text",
        "Publish",
      ],
    },
    content: {
      platform: [
        "Edit page sections",
        "Add text blocks with recommended content",
        "Publish",
      ],
    },
    technical: {
      platform: [
        "Settings → Custom Code for head snippets",
        "Or Marketing & SEO → SEO Settings for site-wide options",
        "Apply fix and publish",
      ],
    },
    entity: {
      platform: [
        "Add Organization schema via Custom Code in Head",
        "Update business info in Wix dashboard to match entity data",
        "Publish",
      ],
    },
    competitor: {
      platform: [
        "Create a new page for the recommended topic",
        "Optimize SEO settings for target keyword",
        "Publish and promote internally",
      ],
    },
  },
  Squarespace: {
    schema: {
      platform: [
        "Settings → Advanced → Code Injection",
        "Paste schema in Header",
        "Save",
      ],
    },
    faq: {
      platform: [
        "Add FAQ accordion block on the page",
        "Inject FAQ JSON-LD via Code Injection → Header",
        "Save",
      ],
    },
    meta: {
      platform: [
        "Pages → page settings → SEO",
        "Edit SEO Description",
        "Save",
      ],
    },
    title: {
      platform: [
        "Page settings → SEO → SEO Title",
        "Enter generated title",
        "Save",
      ],
    },
    h1: {
      platform: [
        "Edit page — use a single H1-style heading block",
        "Enter recommended heading text",
        "Save",
      ],
    },
    content: {
      platform: [
        "Edit page content blocks",
        "Add recommended copy",
        "Save",
      ],
    },
    technical: {
      platform: [
        "Settings → Advanced → Code Injection for head code",
        "Or use URL mappings for redirect fixes",
        "Save",
      ],
    },
    entity: {
      platform: [
        "Code Injection → Header for Organization schema",
        "Update Business Information to match entity",
        "Save",
      ],
    },
    competitor: {
      platform: [
        "Create new page for gap topic",
        "Optimize SEO title and description",
        "Save",
      ],
    },
  },
  Webflow: {
    schema: {
      platform: [
        "Project Settings → Custom Code → Head Code",
        "Paste schema JSON-LD",
        "Publish site",
      ],
    },
    faq: {
      platform: [
        "Add FAQ section on the page",
        "Paste FAQ schema in page or site Head custom code",
        "Publish",
      ],
    },
    meta: {
      platform: [
        "Page settings → SEO settings",
        "Set Meta description",
        "Publish",
      ],
    },
    title: {
      platform: [
        "Page SEO settings → Title tag",
        "Enter generated title",
        "Publish",
      ],
    },
    h1: {
      platform: [
        "Edit the H1 element on the page",
        "Set text to the recommended heading",
        "Publish",
      ],
    },
    content: {
      platform: [
        "Edit rich text or CMS content",
        "Add recommended copy",
        "Publish",
      ],
    },
    technical: {
      platform: [
        "Use Project or Page custom code for head fixes",
        "Or 301 redirects in Hosting settings",
        "Publish",
      ],
    },
    entity: {
      platform: [
        "Add Organization schema in site Head custom code",
        "Align footer/business info with Wikidata sameAs",
        "Publish",
      ],
    },
    competitor: {
      platform: [
        "Create CMS or static page for gap topic",
        "Set SEO fields and internal links",
        "Publish",
      ],
    },
  },
  "Custom HTML": {
    schema: {
      platform: [
        "Open your site template or static HTML file",
        "Paste schema JSON-LD inside <head> before </head>",
        "Deploy to your host",
      ],
    },
    faq: {
      platform: [
        "Add FAQ schema JSON-LD in <head> or before </body>",
        "Ensure visible FAQ content matches schema",
        "Deploy",
      ],
    },
    meta: {
      platform: [
        "Locate <head> in your HTML template",
        "Add or update <meta name=\"description\" content=\"...\" />",
        "Deploy",
      ],
    },
    title: {
      platform: [
        "Update the <title> element in <head>",
        "Deploy",
      ],
    },
    h1: {
      platform: [
        "Ensure one <h1> per page with recommended text",
        "Remove duplicate H1s if present",
        "Deploy",
      ],
    },
    content: {
      platform: [
        "Edit the main content area in your HTML or CMS template",
        "Add recommended sections",
        "Deploy",
      ],
    },
    technical: {
      platform: [
        "Apply server config (.htaccess, nginx) or template change as indicated",
        "Test on staging then deploy",
      ],
    },
    entity: {
      platform: [
        "Add Organization JSON-LD with sameAs URLs in <head>",
        "Align visible NAP and social links on the page",
        "Deploy",
      ],
    },
    competitor: {
      platform: [
        "Create a new HTML page or route for the gap topic",
        "Link from navigation or related pages",
        "Deploy",
      ],
    },
  },
  Other: {
    schema: { platform: ["Add the schema JSON-LD to your site's <head> section via your platform's custom code area.", "Save and verify with Google Rich Results Test."] },
    faq: { platform: ["Add FAQ schema where your platform allows custom head or page code.", "Match visible FAQs to schema content.", "Save and test."] },
    meta: { platform: ["Find SEO or page metadata settings in your CMS.", "Update the meta description field.", "Save."] },
    title: { platform: ["Update the page or site title field in your CMS SEO settings.", "Keep under ~60 characters.", "Save."] },
    h1: { platform: ["Set a single main H1 on the page with the recommended text.", "Save."] },
    content: { platform: ["Edit the page body and add the recommended content.", "Save."] },
    technical: { platform: ["Apply the fix in your platform's code injection, redirects, or server config area.", "Test the live URL."] },
    entity: { platform: ["Add Organization schema and align business entity data across your site.", "Link Wikidata via sameAs if applicable."] },
    competitor: { platform: ["Create or optimize a page targeting the gap from your action plan.", "Save and monitor."] },
  },
};

/**
 * Steps for fix types that are largely platform-agnostic or handled outside the CMS
 * (Google Search Console, Google Business Profile, site-root files, bulk CSV imports).
 * These take precedence over the per-CMS steps so, e.g., a WordPress user asking how to
 * apply a disavow file gets the correct GSC flow rather than theme-editor steps.
 */
const UNIVERSAL_STEPS: Partial<Record<IssueFixType, CmsSteps>> = {
  disavow: {
    platform: [
      "Review the flagged domains first — disavow is a last resort, so remove only genuinely toxic links.",
      "Download the generated disavow file (it's already in Google's required format).",
      "Open the Google Disavow Tool (search.google.com/search-console/disavow-links).",
      "Select your property, click Upload disavow list, and upload the file.",
      "Allow a few weeks for Google to reprocess the links.",
    ],
  },
  local: {
    platform: [
      "Sign in to your Google Business Profile (business.google.com).",
      "Complete every flagged field: categories, description, hours, phone, website, and photos.",
      "Make sure your Name, Address, Phone match your website and the generated LocalBusiness schema exactly.",
      "Add the generated LocalBusiness schema to your site's <head> via your platform's custom-code/head area.",
      "Fix NAP in any inconsistent directory listings before creating new ones.",
    ],
  },
  robots: {
    platform: [
      "Download the generated robots.txt file.",
      "Upload it to your domain ROOT so it loads at https://yourdomain/robots.txt (hosting file manager, FTP, or your platform's robots.txt setting).",
      "Open it in a browser to confirm, then validate in Google Search Console.",
    ],
  },
  llms: {
    platform: [
      "Download the generated llms.txt file.",
      "Upload it to your domain ROOT so it loads at https://yourdomain/llms.txt.",
      "Open it in a browser to confirm it's reachable.",
    ],
  },
  sitemap: {
    platform: [
      "Download the generated sitemap.xml (or enable your SEO plugin's sitemap).",
      "Upload it to your site root, or use the plugin-provided sitemap URL.",
      "Submit the sitemap URL in Google Search Console → Sitemaps.",
    ],
  },
  csv: {
    platform: [
      "Download the generated CSV of recommendations.",
      "Open it in a spreadsheet to review the per-URL title/meta/H1 changes.",
      "Apply in bulk: WordPress (Rank Math/Yoast bulk editor or CSV import), Shopify (bulk editor), or hand to your developer.",
      "Re-crawl or spot-check a few pages after applying.",
    ],
  },
  review: {
    platform: [
      "Add the generated review / aggregate-rating schema to the relevant product or service page <head>.",
      "Only mark up reviews genuinely displayed on that page (Google policy).",
      "Validate with Google's Rich Results Test after publishing.",
    ],
  },
  canonical: {
    platform: [
      "Identify the preferred (canonical) URL for the duplicate set.",
      "Add the generated canonical link tag to the <head> of the duplicate pages (via your SEO plugin's canonical field or head code).",
      "Confirm the canonical points to a live, indexable URL.",
    ],
  },
  redirect: {
    platform: [
      "Pick the right method for your stack: WordPress (Redirection plugin / Rank Math), Shopify (Navigation → URL redirects), or server config (Cloudflare _redirects, Apache .htaccess, nginx).",
      "Add the generated old → new mapping.",
      "Test that the old URL 301-redirects to the new one with no redirect chains.",
    ],
  },
  brief: {
    platform: [
      "Download or copy the generated content brief.",
      "Use it as the outline for the new page: keep the heading structure, target questions, and entities.",
      "Publish, add internal links from related pages, then request indexing.",
    ],
  },
  alt: {
    platform: [
      "Edit each flagged image in your CMS media library or page editor.",
      "Paste the generated descriptive alt text into the image's Alt field (describe the image; don't keyword-stuff).",
      "Save or update the page.",
    ],
  },
};

function formatSteps(label: string, steps: string[]): string {
  return steps.map((s, i) => `${i + 1}. ${s}`).join("\n");
}

/** Fix-type + CMS implementation guide (guide §8 — WordPress schema example). */
export function getCmsImplementationGuide(
  cms: string,
  fixType: IssueFixType,
  issueTitle: string
): string {
  const subject = FIX_LABEL[fixType] || "this fix";
  const intro = `Here's how to add this **${subject}** to **${cms}** for **${issueTitle}**:\n\n`;

  const cmsGuides = CMS_STEPS[cms] || CMS_STEPS.Other;
  // Universal (CMS-agnostic / external) types win over per-CMS steps; then per-CMS type,
  // then the CMS's technical fallback, then the Other-CMS technical fallback.
  const steps =
    UNIVERSAL_STEPS[fixType] ||
    cmsGuides?.[fixType] ||
    cmsGuides?.technical ||
    CMS_STEPS.Other?.technical;

  if (!steps) {
    return `${intro}Tell me a bit more about where you want this applied and I'll give you exact steps.`;
  }

  const parts: string[] = [];

  if (steps.plugin?.length) {
    parts.push(`**Option 1: Using a Plugin (Easiest)**\n${formatSteps("", steps.plugin)}`);
  }
  if (steps.manual?.length) {
    parts.push(`**Option ${steps.plugin ? "2" : "1"}: Manual (Theme Editor)**\n${formatSteps("", steps.manual)}`);
  }
  if (steps.platform?.length) {
    const optNum = (steps.plugin ? 1 : 0) + (steps.manual ? 1 : 0) + 1;
    parts.push(`**${optNum === 1 ? "Steps" : `Option ${optNum}`}:**\n${formatSteps("", steps.platform)}`);
  }

  return `${intro}${parts.join("\n\n")}\n\nNeed more help? Just ask!`;
}
